import React from 'react';
import { CircleCloseIcon, SubscriptionTimerIcon } from 'assets/icons';
import CustomButton, { ButtonType } from 'components/custom-button/CustomButton';
import { VoidFn } from 'types/baseTypes';
import { useAppSelector } from 'hooks';
import { selectIsOwner } from 'containers/app/appSlice';
import { CustomModal } from 'components';
import ModalWrapper from './ModalWrapper';
import useFSMPlanInfo from 'containers/integrations/fsm-integration/hooks/useFSMPlanInfo';

interface FSMSubscriptionEndedModalProps {
  handleClose: VoidFn;
  onRenew: VoidFn;
  showCloseButton: boolean;
  isFullScreenModal?: boolean;
}

const FSMSubscriptionEndedModal: React.FC<FSMSubscriptionEndedModalProps> = ({
  onRenew,
  handleClose,
  showCloseButton,
  isFullScreenModal = true
}) => {
  const isOwner = useAppSelector(selectIsOwner);
  const { subscriptionPrice } = useFSMPlanInfo();

  const handleChatWithUsClick = () => {
    if (window.fcWidget) window.fcWidget.open();
  };

  const renderModalContent = (
    <div className="customTransparentScroll relative overflow-auto rounded-xl bg-white p-6">
      {showCloseButton && <CircleCloseIcon className="absolute top-4 right-4 cursor-pointer" onClick={handleClose} />}
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-secondaryBtn">
        <SubscriptionTimerIcon />
      </div>
      {isOwner ? (
        <>
          <div className="mb-3.5 text-center text-2xl font-bold text-heading">
            Field Serve Plus subscription ended,
            <br />
            <span className="font-bold">renew for {subscriptionPrice} / month</span>
          </div>
          <div className="mb-6 text-center text-lg text-gray-600">
            Please renew your Field Serve subscription for advanced features.
          </div>
        </>
      ) : (
        <>
          <div className="mb-3.5 text-center text-2xl font-bold text-heading">Field Serve Plus subscription ended</div>
          <div className="mb-6 text-center text-lg text-gray-600">
            Please reach out to your business owner to enable the Field Serve subscription.
          </div>
        </>
      )}
      <CustomButton
        className="h-14 w-full text-lg font-semibold"
        type={ButtonType.PRIMARY}
        id="renew-subscription-button"
        onClick={onRenew}>
        {isOwner ? 'Renew subscription' : 'Okay'}
      </CustomButton>
      {isOwner && (
        <div className="mt-6 text-center text-sbase font-medium text-accentText">
          Need help?{' '}
          <button
            type="button"
            onClick={handleChatWithUsClick}
            className="cursor-pointer font-bold text-secondary"
            tabIndex={0}
            aria-label="Chat with us">
            Chat with us
          </button>
        </div>
      )}
    </div>
  );

  return isFullScreenModal ? (
    <CustomModal open={true} closeModal={handleClose} width="max-w-xl md:w-[579px]" className="max-h-[90vh] rounded-xl">
      {renderModalContent}
    </CustomModal>
  ) : (
    <ModalWrapper>{renderModalContent}</ModalWrapper>
  );
};

export default FSMSubscriptionEndedModal;
